import { useCallback, useEffect, useState } from "react";
import {
  BadgePercent,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  MailWarning,
  PackageCheck,
} from "lucide-react";
import OathboundFrame from "@/components/OathboundFrame/OathboundFrame";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DiscountCodesView from "./DiscountCodesView";
import {
  fetchAdminOrders,
  forceAdminEmails,
  formatAdminAmount,
  loginAdmin,
  sendAdminPreorderEmails,
} from "./adminApi";
import "./AdminConsole.css";

const PAGE_SIZE = 25;

function statusClass(status) {
  return `admin-status is-${String(status).toLowerCase().replace(/[^a-z]+/g, "-")}`;
}

export default function AdminConsole() {
  const [phase, setPhase] = useState("checking");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [start, setStart] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [loadFailed, setLoadFailed] = useState(false);
  const [forcingEmails, setForcingEmails] = useState(false);
  const [forceMessage, setForceMessage] = useState("");
  const [emailActionsOpen, setEmailActionsOpen] = useState(false);
  const [activeView, setActiveView] = useState("orders");

  const loadOrders = useCallback(async (nextStart, checkingSession = false) => {
    setPhase("loading");
    setLoadFailed(false);
    try {
      const result = await fetchAdminOrders(nextStart);
      setOrders(Array.isArray(result?.orders) ? result.orders : []);
      setStart(result?.start ?? nextStart);
      setHasNext(Boolean(result?.hasNext));
      setSelected(new Set());
      setForceMessage("");
      setEmailActionsOpen(false);
      setPhase("orders");
      return true;
    } catch (error) {
      if (checkingSession || error?.status === 401) {
        setPhase("login");
      } else {
        setLoadFailed(true);
        setPhase("orders");
      }
      return false;
    }
  }, []);

  useEffect(() => {
    loadOrders(1, true);
  }, [loadOrders]);

  const handleUnauthorized = useCallback(() => {
    setEmailActionsOpen(false);
    setPhase("login");
  }, []);

  const submitLogin = async (event) => {
    event.preventDefault();
    if (phase === "authenticating") return;

    setPhase("authenticating");
    try {
      await loginAdmin(username, password);
      setPassword("");
      await loadOrders(1);
    } catch {
      // Deliberately disclose neither which credential failed nor whether the
      // current client has been throttled.
      setPassword("");
      setPhase("login");
    }
  };

  const toggleOrder = (orderId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const forceEmails = async () => {
    if (!selected.size || forcingEmails) return;
    setForcingEmails(true);
    setForceMessage("");
    try {
      const result = await forceAdminEmails([...selected]);
      setForceMessage(
        result.claimed === 0
          ? "No unsent emails."
          : `${result.sent} email${result.sent === 1 ? "" : "s"} sent${
              result.failed ? `, ${result.failed} failed` : ""
            }.`,
      );
      setEmailActionsOpen(false);
    } catch (error) {
      setEmailActionsOpen(false);
      if (error?.status === 401) {
        setPhase("login");
      } else {
        setForceMessage("Emails could not be sent.");
      }
    } finally {
      setForcingEmails(false);
    }
  };

  const sendPreorderEmails = async () => {
    if (!selected.size || forcingEmails) return;
    setForcingEmails(true);
    setForceMessage("");
    try {
      const result = await sendAdminPreorderEmails([...selected]);
      setForceMessage(
        result.claimed === 0
          ? "No paid preorder emails awaiting delivery."
          : `${result.sent} preorder email${result.sent === 1 ? "" : "s"} sent${
              result.failed ? `, ${result.failed} failed` : ""
            }.`,
      );
      setEmailActionsOpen(false);
    } catch (error) {
      setEmailActionsOpen(false);
      if (error?.status === 401) {
        setPhase("login");
      } else {
        setForceMessage("Preorder emails could not be sent.");
      }
    } finally {
      setForcingEmails(false);
    }
  };

  const lastVisible = orders.length ? start + orders.length - 1 : start;
  const showLogin = phase === "login" || phase === "authenticating";
  const initializing =
    (phase === "checking" || phase === "loading") && orders.length === 0 && start === 1;

  return (
    <main
      className="admin-console-page"
      aria-labelledby={activeView === "discounts" && !showLogin
        ? "admin-discounts-title"
        : "admin-console-title"}
    >
      <OathboundFrame />

      <section className={`admin-console-content${showLogin ? " is-login" : ""}`}>
        {initializing ? null : showLogin ? (
          <form className="admin-login-card" onSubmit={submitLogin}>
            <p className="admin-console-eyebrow">Private access</p>
            <h1 id="admin-console-title">Admin console</h1>

            <label>
              <span>Username</span>
              <input
                type="text"
                name="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                maxLength={200}
                required
                autoFocus
              />
            </label>

            <label>
              <span>Password</span>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                maxLength={200}
                required
              />
            </label>

            <button type="submit" disabled={phase === "authenticating"}>
              Enter
            </button>
          </form>
        ) : (
          <div className="admin-console-workspace">
            <nav className="admin-console-tabs" aria-label="Admin sections">
              <button
                type="button"
                className={activeView === "orders" ? "is-active" : ""}
                aria-current={activeView === "orders" ? "page" : undefined}
                onClick={() => setActiveView("orders")}
              >
                <PackageCheck aria-hidden="true" />
                <span>Orders &amp; email</span>
              </button>
              <button
                type="button"
                className={activeView === "discounts" ? "is-active" : ""}
                aria-current={activeView === "discounts" ? "page" : undefined}
                onClick={() => {
                  setEmailActionsOpen(false);
                  setActiveView("discounts");
                }}
              >
                <BadgePercent aria-hidden="true" />
                <span>Discount codes</span>
              </button>
            </nav>

            {activeView === "orders" ? (
              <div className="admin-orders-shell">
                <header className="admin-orders-heading">
              <div>
                <p className="admin-console-eyebrow">Protected orders</p>
                <h1 id="admin-console-title">Orders</h1>
              </div>
              <div className="admin-orders-actions">
                <p>{forceMessage || `${selected.size} selected`}</p>
                <button
                  type="button"
                  disabled={!selected.size || forcingEmails}
                  onClick={() => setEmailActionsOpen(true)}
                >
                  {forcingEmails ? "Sending…" : "Email actions"}
                </button>
              </div>
                </header>

                <div className="admin-orders-table-wrap">
              <table className="admin-orders-table">
                <thead>
                  <tr>
                    <th scope="col" aria-label="Select order" />
                    <th scope="col">Order ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Amount paid</th>
                    <th scope="col">Payment</th>
                    <th scope="col">Fulfilment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.orderId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(order.orderId)}
                          onChange={() => toggleOrder(order.orderId)}
                          aria-label={`Select order ${order.orderId}`}
                        />
                      </td>
                      <td>#{order.orderId}</td>
                      <td>{order.customerName}</td>
                      <td>
                        {Number.isInteger(order.amountPaidPaise)
                          ? formatAdminAmount(order.amountPaidPaise, order.currency)
                          : "—"}
                      </td>
                      <td><span className={statusClass(order.paymentStatus)}>{order.paymentStatus}</span></td>
                      <td><span className={statusClass(order.fulfillmentStatus)}>{order.fulfillmentStatus}</span></td>
                    </tr>
                  ))}
                  {!orders.length && (
                    <tr>
                      <td className="admin-orders-empty" colSpan="6">
                        {loadFailed ? "Orders could not be loaded." : "No orders in this range."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
                </div>

                <footer className="admin-orders-pagination">
              <button
                type="button"
                aria-label="Previous 25 orders"
                disabled={start === 1 || phase === "loading"}
                onClick={() => loadOrders(Math.max(1, start - PAGE_SIZE))}
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <span>{start}–{lastVisible}</span>
              <button
                type="button"
                aria-label="Next 25 orders"
                disabled={!hasNext || phase === "loading"}
                onClick={() => loadOrders(start + PAGE_SIZE)}
              >
                <ChevronRight aria-hidden="true" />
              </button>
                </footer>
              </div>
            ) : (
              <DiscountCodesView onUnauthorized={handleUnauthorized} />
            )}
          </div>
        )}
      </section>

      <Dialog
        open={emailActionsOpen}
        onOpenChange={(open) => {
          if (!forcingEmails) setEmailActionsOpen(open);
        }}
      >
        <DialogContent className="admin-email-dialog">
          <DialogHeader className="admin-email-dialog-header">
            <p className="admin-console-eyebrow">Selected orders</p>
            <DialogTitle>Email actions</DialogTitle>
            <DialogDescription className="admin-email-dialog-description">
              Choose an action for {selected.size} selected order{selected.size === 1 ? "" : "s"}.
            </DialogDescription>
          </DialogHeader>

          <div className="admin-email-options">
            <button type="button" disabled={forcingEmails} onClick={forceEmails}>
              <MailWarning aria-hidden="true" />
              <span>
                <strong>Force stuck emails</strong>
                <small>Retry any unsent order, fulfilment, or preorder email. Payment status is ignored.</small>
              </span>
            </button>
            <button type="button" disabled={forcingEmails} onClick={sendPreorderEmails}>
              <CalendarClock aria-hidden="true" />
              <span>
                <strong>Send preorder timelines email</strong>
                <small>Create and send the 1 September delivery update to paid orders only.</small>
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
